import * as React from 'react';
import {
  FormEvent,
  FunctionComponent,
  ReactNode,
  useContext,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AutocompleteRenderInputParams,
  FormHelperText,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { FormikProps, useFormik } from 'formik';
import get from 'lodash/get';

import { useAppDispatch } from '../../../../../store/hooks';
import { useAppSelector } from '../../../../../store/store';
import {
  APPS_FEATURE,
  APPS_FILES,
} from '../../../../../store/utils/files-names';
import { ReactComponent as CheckmarkIcon } from '../../../../shared/assets/icons/snackbar-icons/check-mark.svg';
import { ReactComponent as WarningIcon } from '../../../../shared/assets/icons/snackbar-icons/warning.svg';
import { ChangeDetectorContext } from '../../../../shared/components/change-detector-context/ChangeDetectorContext';
import { ChangeDetectorContextModel } from '../../../../shared/components/change-detector-context/models/change-detector-context.model';
import DragAndDrop from '../../../../shared/components/drag-and-drop/DragAndDrop';
import Dropdown from '../../../../shared/components/dropdown/Dropdown';
import { ItemLabelModel } from '../../../../shared/components/dropdown/models/dropdown-props.model';
import Modal from '../../../../shared/components/modal/Modal';
import { ModalContext } from '../../../../shared/components/modal/ModalContext';
import { ModalContextModel } from '../../../../shared/components/modal/models/modal-context.model';
import { SnackBarContext } from '../../../../shared/components/snack-bar-context/SnackBarContext';
import { SnackBarContextModel } from '../../../../shared/components/snack-bar-context/models/snack-bar-context.model';
import Autocomplete from '../../../../shared/components/ui/Autocomplete/Autocomplete';
import Box from '../../../../shared/components/ui/Box/Box';
import Button from '../../../../shared/components/ui/Button/Button';
import CharacterCounter from '../../../../shared/components/ui/CharacterCounter/CharacterCounter';
import FormControl from '../../../../shared/components/ui/FormControl/FormControl';
import Input from '../../../../shared/components/ui/Input/Input';
import { FeaturedEnum } from '../../../../shared/enums/featured.enum';
import { FileMetadataTypesEnum } from '../../../../shared/enums/file-metadata-types.enum';
import useChangePrompt from '../../../../shared/hooks/use-change-prompt/use-change-prompt.hook';
import { useCheckPath } from '../../../../shared/hooks/use-check-path/use-check-path.hook';
import { IdFile } from '../../../../shared/models/id-file.model';
import { formTheme } from '../../../../shared/themes/form-theme/form.theme';
import { castTo } from '../../../../shared/utils/cast-to';
import {
  mapCategoryDtoToItemLabelModel,
  mapCountryRegionDtoToItemLabelModel,
  mapFeatureEnumToItemLabelModel,
  mapLanguageDtoToItemLabelModel,
} from '../../../../shared/utils/mapping/mapping.utils';
import { styleDropdown } from '../../../../shared/utils/style-dropdown/style-dropdown.util';
import { AppRequestModel } from '../../../models/app-request.model';
import { appsFilesActions } from '../../../store/slices/apps-files.slice';
import { createAppDetail } from '../../../thunks/save-app-detail-form.thunk';
import { updateApp } from '../../../thunks/update-app.thunk';
import { appModelToFormik } from '../../../utils/app-model-to-app-detail-form-formik-model.util';
import { useIsFormModified } from './hooks/use-is-form-modified/use-is-form-modified.hook';
import { usePopulateAppDetailsForm } from './hooks/use-populate-app-details-form/use-populate-app-details-form';
import { useSyncFormikValuesWithExternalState } from './hooks/use-sync-formik-values-with-external-state/use-sync-formik-values-with-external-state.hook';
import { AppDetailFormFormikModel } from './models/app-detail-form-formik.model';
import { getAppDetailsFormSchema } from './schemas/app-details-form.schema';

const AppDetailsForm: FunctionComponent = () => {
  const screenshotsIds: number[] = useAppSelector(
    state => get(state, `${APPS_FEATURE}.${APPS_FILES}`).screenshots
  );
  const iconId: number = useAppSelector(
    state => get(state, `${APPS_FEATURE}.${APPS_FILES}`).icon
  );

  const cdContext = useContext(
    ChangeDetectorContext
  ) as ChangeDetectorContextModel;
  const ctx = useContext(ModalContext) as ModalContextModel;
  const snackBarContext = useContext(SnackBarContext) as SnackBarContextModel;
  const [iconFile, setIconFile] = useState<IdFile[] | undefined>();
  const [screenshotsFiles, setScreenshotFiles] = useState<
    IdFile[] | undefined
  >();
  const [isOnEdit, { id: appId }] = useCheckPath('/appstore/all-apps/edit/');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const cancelAction = useChangePrompt(
    true,
    () => {
      navigate('../../all-apps');
    },
    <span>Warning</span>,
    <span>Filled information will be lost. Leave anyway?</span>
  );

  const optionsFeatured = [FeaturedEnum.Yes, FeaturedEnum.No];

  const formik: FormikProps<AppDetailFormFormikModel> =
    useFormik<AppDetailFormFormikModel>({
      initialValues: {
        categoriesIds: [],
        countriesRegionsIds: [],
        defaultLanguageIso: null,
        iconId: null,
        longDescription: '',
        name: '',
        privacyPolicy: '',
        publisherName: '',
        screenshotsIds: [],
        shortDescription: '',
        website: '',
        packageName: '',
        isFeatured: false,
      },
      validationSchema: getAppDetailsFormSchema(),
      onSubmit: values => {
        const formObject: AppRequestModel = {
          categoriesIds: values.categoriesIds.map(item => item.id),
          countriesRegionsIds: values.countriesRegionsIds.map(item => item.id),
          defaultLanguageIso: values.defaultLanguageIso?.id as string,
          iconId: iconId,
          longDescription: values.longDescription,
          name: values.name,
          privacyPolicy: values.privacyPolicy,
          publisherName: values.publisherName,
          screenshotsIds: screenshotsIds,
          shortDescription: values.shortDescription,
          website: values.website,
          isFeatured: values.isFeatured,
          packageName: values.packageName,
        };
        if (isOnEdit) {
          dispatch(
            updateApp({
              id: parseInt(appId as string, 10),
              ...formObject,
            })
          )
            .unwrap()
            .then(data => {
              formik.setValues(appModelToFormik(data));
              snackBarContext.changeMessage('Successfully updated the app !');
              snackBarContext.changeIcon(<CheckmarkIcon />);
              cdContext?.setIsChanged(false);
            })
            .catch(() => {
              snackBarContext.changeSeverity('error');
              snackBarContext.changeIcon(<WarningIcon />);
              snackBarContext.changeMessage(
                'Something went wrong while updating the App'
              );
            })
            .finally(() => {
              snackBarContext.isOpen(true);
            });
        } else {
          dispatch(createAppDetail(formObject))
            .unwrap()
            .then(value => {
              snackBarContext.changeMessage('Successfully created the app !');
              snackBarContext.changeIcon(<CheckmarkIcon />);
              cdContext?.setIsChanged(false);
              navigate(
                `/appstore/all-apps/edit/${value.id}/releases?appCreated=1`,
                {
                  replace: true,
                }
              );
            })
            .catch(() => {
              snackBarContext.changeSeverity('error');
              snackBarContext.changeIcon(<WarningIcon />);
              snackBarContext.changeMessage(
                'Something went wrong while creating the App'
              );
            })
            .finally(() => {
              snackBarContext.isOpen(true);
            });
        }
      },
      validateOnBlur: true,
      validateOnChange: true,
      validateOnMount: false,
    });

  usePopulateAppDetailsForm(
    isOnEdit,
    formik,
    setIconFile,
    setScreenshotFiles,
    appId
  );

  useSyncFormikValuesWithExternalState(iconId, screenshotsIds, formik);

  useIsFormModified(iconId, screenshotsIds, formik.values, cdContext);

  return (
    <>
      <ThemeProvider theme={formTheme}>
        <Box
          sx={{
            my: '48px',
          }}
          data-testid="create-edit-app-details"
        >
          <Box
            component={'form'}
            onSubmit={event =>
              formik.handleSubmit(castTo<FormEvent<HTMLFormElement>>(event))
            }
            sx={{
              maxWidth: '500px',
              margin: 'auto',
              width: '100%',
              px: '6px',
              boxSizing: 'border-box',
            }}
          >
            <FormControl>
              <Input
                id="name"
                label="App name"
                variant="filled"
                name="name"
                type="text"
                fullWidth={true}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.name && !!formik.errors.name}
                helperText={
                  !!formik.errors.name &&
                  formik.touched.name &&
                  formik.errors.name
                }
                value={formik.values.name}
                inputProps={{
                  maxLength: '30',
                }}
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
              <CharacterCounter
                className={'input-helper-text'}
                current={formik.values.name.length}
                max={30}
              />
              <Typography className={'input-helper-text'} component={'div'}>
                This is how your app will appear on SPARQ store. It should be
                concise and not include price, rank, any emoji or repetitive
                symbols.
              </Typography>
            </FormControl>

            <FormControl>
              <Input
                label="Package name"
                variant="filled"
                id="packageName"
                name="packageName"
                fullWidth={true}
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.packageName}
                error={
                  formik.touched.packageName && !!formik.errors.packageName
                }
                helperText={
                  !!formik.errors.packageName &&
                  formik.touched.packageName &&
                  formik.errors.packageName
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <Input
                label="Publisher name"
                variant="filled"
                id="publisherName"
                name="publisherName"
                fullWidth={true}
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.publisherName}
                error={
                  formik.touched.publisherName && !!formik.errors.publisherName
                }
                helperText={
                  !!formik.errors.publisherName &&
                  formik.touched.publisherName &&
                  formik.errors.publisherName
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <Autocomplete
                className={'dropdown-autocomplete'}
                id="isFeatured"
                sx={{ width: '100%' }}
                value={{
                  id: +formik.values.isFeatured,
                  label: FeaturedEnum[+formik.values.isFeatured],
                }}
                onBlur={formik.handleBlur}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(event, newInputValue) => {
                  const inputValue =
                    castTo<keyof typeof FeaturedEnum>(newInputValue);
                  formik.setFieldValue(
                    'isFeatured',
                    !!FeaturedEnum[inputValue]
                  );
                }}
                renderInput={function (
                  params: AutocompleteRenderInputParams
                ): ReactNode {
                  return (
                    <Input {...params} label="Featured" variant="filled" />
                  );
                }}
                options={optionsFeatured.map(mapFeatureEnumToItemLabelModel)}
              />
            </FormControl>

            <FormControl>
              <Dropdown
                endpoint="/languages"
                dataMap={mapLanguageDtoToItemLabelModel}
                autoComplete={{
                  className: 'dropdown-autocomplete',
                  autoComplete: true,
                  sx: {
                    width: '488px',
                  },
                  onChange: (event, defaultLanguageValue) => {
                    formik.setFieldValue(
                      'defaultLanguageIso',
                      defaultLanguageValue
                    );
                  },
                  onBlur: formik.handleBlur,
                  id: 'defaultLanguageIso',
                  value: formik.values.defaultLanguageIso,
                  isOptionEqualToValue: (option, value) =>
                    option.id === value.id,
                  renderInput: params => {
                    const newParams = styleDropdown(params);
                    return (
                      <Input
                        {...newParams}
                        label="Default language"
                        variant="filled"
                        helperText={
                          !!formik.errors.defaultLanguageIso &&
                          formik.touched.defaultLanguageIso &&
                          formik.errors.defaultLanguageIso
                        }
                        error={
                          formik.touched.defaultLanguageIso &&
                          !!formik.errors.defaultLanguageIso
                        }
                        InputLabelProps={{
                          required: true,
                        }}
                      />
                    );
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <Dropdown
                endpoint="/categories"
                dataMap={mapCategoryDtoToItemLabelModel}
                autoComplete={{
                  className: 'dropdown-autocomplete',
                  sx: {
                    width: '100%',
                    '& .MuiFilledInput-root': {
                      paddingTop: '24px',
                      paddingBottom: '5px',
                    },
                  },
                  onChange: (
                    event,
                    categoryValue: (string | ItemLabelModel<number>)[]
                  ) => {
                    formik.setFieldValue('categoriesIds', categoryValue);
                  },
                  onBlur: formik.handleBlur,
                  id: 'categoriesIds',
                  multiple: true,
                  value: formik.values.categoriesIds,
                  isOptionEqualToValue: (option, value) =>
                    option.id === value.id,
                  renderInput: params => {
                    const newParams = styleDropdown(params);
                    return (
                      <Input
                        {...newParams}
                        label="Category"
                        InputLabelProps={{
                          required: true,
                        }}
                        variant="filled"
                        name={'categoriesIds'}
                        helperText={
                          !!formik.errors.categoriesIds &&
                          formik.touched.categoriesIds &&
                          formik.errors.categoriesIds
                        }
                        error={
                          formik.touched.categoriesIds &&
                          !!formik.errors.categoriesIds
                        }
                      />
                    );
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <Input
                label="Website URL"
                variant="filled"
                id="website"
                fullWidth={true}
                name="website"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.website}
                error={formik.touched.website && !!formik.errors.website}
                helperText={
                  !!formik.errors.website &&
                  formik.touched.website &&
                  formik.errors.website
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                InputLabelProps={{
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>

            <FormControl sx={{ flexWrap: 'wrap' }}>
              <Input
                label="Privacy policy URL"
                variant="filled"
                id="privacyPolicy"
                name="privacyPolicy"
                type="text"
                fullWidth={true}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={
                  formik.touched.privacyPolicy && !!formik.errors.privacyPolicy
                }
                helperText={
                  !!formik.errors.privacyPolicy &&
                  formik.touched.privacyPolicy &&
                  formik.errors.privacyPolicy
                }
                value={formik.values.privacyPolicy}
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>

            <FormControl key="app-country-regions">
              <Dropdown
                endpoint="/country-regions"
                dataMap={mapCountryRegionDtoToItemLabelModel}
                autoComplete={{
                  className: 'dropdown-autocomplete',
                  sx: {
                    width: '100%',
                    '& .MuiFilledInput-root': {
                      paddingTop: '24px',
                      paddingBottom: '5px',
                    },
                  },
                  onChange: (
                    event,
                    countryValue: (string | ItemLabelModel<number>)[]
                  ) => {
                    formik.setFieldValue('countriesRegionsIds', countryValue);
                  },
                  onBlur: formik.handleBlur,
                  id: 'countriesRegionsIds',
                  value: formik.values.countriesRegionsIds,
                  isOptionEqualToValue: (option, value) =>
                    option.id === value.id,
                  multiple: true,
                  renderInput: params => {
                    const newParams = styleDropdown(params);
                    return (
                      <Input
                        {...newParams}
                        label="Country and region"
                        InputLabelProps={{
                          required: true,
                        }}
                        variant="filled"
                        helperText={
                          !!formik.errors.countriesRegionsIds &&
                          formik.touched.countriesRegionsIds &&
                          formik.errors.countriesRegionsIds
                        }
                        error={
                          formik.touched.countriesRegionsIds &&
                          !!formik.errors.countriesRegionsIds
                        }
                      />
                    );
                  },
                }}
              />
            </FormControl>

            <FormControl sx={{ flexWrap: 'wrap' }}>
              <Input
                label="Short description"
                variant="filled"
                id="shortDescription"
                name="shortDescription"
                type="text"
                fullWidth={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.shortDescription}
                error={
                  formik.touched.shortDescription &&
                  !!formik.errors.shortDescription
                }
                helperText={
                  !!formik.errors.shortDescription &&
                  formik.touched.shortDescription &&
                  formik.errors.shortDescription
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                inputProps={{
                  maxLength: '100',
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
              <CharacterCounter
                className={'input-helper-text'}
                current={formik.values.shortDescription.length}
                max={100}
              />
              <Typography className={'input-helper-text'}>
                A short description for your app. Users can expand to view your
                full description.
              </Typography>
            </FormControl>

            <FormControl>
              <Input
                label="Full description"
                variant="filled"
                id="longDescription"
                name="longDescription"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                multiline={true}
                fullWidth={true}
                rows={9}
                value={formik.values.longDescription}
                error={
                  formik.touched.longDescription &&
                  !!formik.errors.longDescription
                }
                helperText={
                  !!formik.errors.longDescription &&
                  formik.touched.longDescription &&
                  formik.errors.longDescription
                }
                inputProps={{
                  maxLength: '4000',
                }}
                InputProps={{
                  id: 'longDescription',
                  name: 'longDescription',
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />

              <CharacterCounter
                className={'input-helper-text'}
                current={formik.values.longDescription.length}
                max={4000}
              />
            </FormControl>

            <FormControl
              sx={{
                flexWrap: 'wrap',
              }}
            >
              <DragAndDrop
                name="AppDetailsFormDnD1"
                key={'icon-drag-and-drop'}
                removeHandler={appsFilesActions.deleteIcon}
                uploadHandler={appsFilesActions.setIcon}
                multiple={false}
                files={iconFile}
                label="App icon"
                isRequired={true}
                hasError={formik.touched.iconId && !!formik.errors.iconId}
                fileType={FileMetadataTypesEnum.ICON}
                buttonLabel={iconId ? 'Change Icon' : 'Upload Icon'}
                helperText="Drop a PNG, JPEG or WEBP file here to upload"
                fileTypeAccepted={'.PNG, .jpeg, .webp'}
                previewType={FileMetadataTypesEnum.ICON}
              ></DragAndDrop>

              {formik.touched.iconId && !!formik.errors.iconId && (
                <FormHelperText error>{formik.errors.iconId}</FormHelperText>
              )}
            </FormControl>
            <FormControl sx={{ flexWrap: 'wrap' }}>
              <DragAndDrop
                name="AppDetailsFormDnD2"
                key={'screenshots-drag-and-drop'}
                removeHandler={appsFilesActions.deleteScreenshots}
                uploadHandler={appsFilesActions.setScreenshots}
                multiple={true}
                files={screenshotsFiles}
                label="App screenshots"
                isRequired={true}
                hasError={
                  formik.touched.screenshotsIds &&
                  !!formik.errors.screenshotsIds
                }
                fileType={FileMetadataTypesEnum.SCREENSHOT}
                helperText="Drop a PNG, JPEG or WEBP file here to upload"
                buttonLabel={'Browse files'}
                fileTypeAccepted={'.png, .jpeg, .webp'}
                previewType={FileMetadataTypesEnum.SCREENSHOT}
              ></DragAndDrop>

              {formik.touched.screenshotsIds &&
                !!formik.errors.screenshotsIds && (
                  <FormHelperText error>
                    {formik.errors.screenshotsIds}
                  </FormHelperText>
                )}

              <Typography className={'input-helper-text'}>
                This will help promote your app in different places on SPARQ
                store. In case of cropping, avoid placing text near the edges
                and centre key visuals. PNG, JPEG or WEBP, 1.024 px by 500 px,
                up to 1 MB.
              </Typography>
            </FormControl>

            <FormControl
              classes={{
                root: 'button-container',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  type="button"
                  variant="contained"
                  classes={{
                    root: 'button',
                  }}
                  className="btn btn-white"
                  onClick={cancelAction}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  classes={{
                    root: 'button',
                  }}
                  className="btn btn-primary"
                >
                  Save
                </Button>
              </Box>
            </FormControl>
          </Box>
        </Box>
      </ThemeProvider>

      <Modal>{ctx.component}</Modal>
    </>
  );
};
export default AppDetailsForm;
